import React, { useState, useEffect, useRef } from 'react'
import { useGameplayContext } from '../contexts/gameplayContext'
import {
  formatCurrency,
  formatDuration,
  saveGameDataLocal,
  isProductionBuild,
  calculateOfflineStoke,
  getServerTime
} from '../utils'
import { Button } from '@nextui-org/react'

// Minimum time away (in minutes) required to show the welcome back screen
const MIN_AWAY_TIME_MINUTES = 4

export const WelcomeBackScreen: React.FC = () => {
  const { gameData, earnStoke, setGameData, saveToCloud } = useGameplayContext()
  const [offlineStoke, setOfflineStoke] = useState<number | null>(null)
  const [showWelcomeBack, setShowWelcomeBack] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [timeAwayInMinutes, setTimeAwayInMinutes] = useState<number>(0)

  // Keep track of when the tab becomes hidden
  const lastActiveTimeRef = useRef<Date | null>(null)

  // Check for offline time on component mount
  useEffect(() => {
    const checkOfflineTime = async () => {
      try {
        setIsLoading(true)

        if (!gameData.lastSaveTime) {
          setIsLoading(false)
          return
        }

        const isGreenWaveUnlocked = gameData.skills.green_wave_riding?.level > 0
        if (!isGreenWaveUnlocked) {
          setIsLoading(false)
          return
        }

        const serverTime = await getServerTime()
        const lastSaveDate = new Date(gameData.lastSaveTime)
        const currentDate = new Date(serverTime)
        const diffMs = currentDate.getTime() - lastSaveDate.getTime()
        const diffMinutes = diffMs / (1000 * 60)
        setTimeAwayInMinutes(diffMinutes)

        const shouldShowWelcome = diffMinutes >= MIN_AWAY_TIME_MINUTES
        if (shouldShowWelcome) {
          const stokeEarned = calculateOfflineStoke(
            gameData.lastSaveTime,
            serverTime,
            gameData.currencies.stoke.totalMultiplier || 1,
            gameData.currencies.stoke.max || 1
          )
          setOfflineStoke(stokeEarned)
          setShowWelcomeBack(true)
        }
      } catch (error) {
        // Silent error handling
      } finally {
        setIsLoading(false)
      }
    }
    checkOfflineTime()
  }, [])

  // Set up visibilitychange event listener
  useEffect(() => {
    const handleVisibilityChange = async () => {
      const isGreenWaveUnlocked = gameData.skills.green_wave_riding?.level > 0
      if (!isGreenWaveUnlocked) {
        return
      }
      if (document.visibilityState === 'hidden') {
        lastActiveTimeRef.current = new Date()
      } else if (document.visibilityState === 'visible' && lastActiveTimeRef.current) {
        const now = new Date()
        const awayTimeMs = now.getTime() - lastActiveTimeRef.current.getTime()
        const awayTimeMinutes = awayTimeMs / (1000 * 60)
        if (awayTimeMinutes >= MIN_AWAY_TIME_MINUTES) {
          setIsLoading(true)
          try {
            const serverTime = await getServerTime()
            const tabInactiveStoke = calculateOfflineStoke(
              gameData.lastSaveTime,
              serverTime,
              gameData.currencies.stoke.totalMultiplier || 1,
              gameData.currencies.stoke.max || 1
            )
            setOfflineStoke(tabInactiveStoke)
            setTimeAwayInMinutes(awayTimeMinutes)
            setShowWelcomeBack(true)
          } catch (error) {
            // Silent error handling
          } finally {
            setIsLoading(false)
          }
        }
        lastActiveTimeRef.current = null
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [
    gameData.skills.green_wave_riding,
    gameData.lastSaveTime,
    gameData.currencies.stoke.totalMultiplier,
    gameData.currencies.stoke.max
  ])

  const handleCollectStoke = async () => {
    if (offlineStoke !== null && offlineStoke > 0) {
      try {
        // Get current server time
        const currentTime = await getServerTime()

        // Create updated game data
        const updatedGameData = {
          ...gameData,
          currencies: {
            ...gameData.currencies,
            stoke: {
              ...gameData.currencies.stoke,
              amount: Math.min(gameData.currencies.stoke.amount + offlineStoke, gameData.currencies.stoke.max || 1)
            }
          },
          lastSaveTime: currentTime
        }

        // Update state
        setGameData(updatedGameData)

        // Save to local storage first
        saveGameDataLocal(updatedGameData)

        // Save to cloud if in production
        if (isProductionBuild()) {
          await saveToCloud()
        }
      } catch (error) {
        // Fallback to direct stoke earning in case of error
        earnStoke(offlineStoke)
      }
    }

    // Close the welcome back screen
    setShowWelcomeBack(false)
  }

  if (isLoading || !showWelcomeBack) {
    return null
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/70 z-[9999]">
      <div className="bg-gradient-to-b from-blue-800 to-blue-600 p-6 rounded-lg shadow-xl max-w-md w-full text-white">
        <h2 className="text-2xl font-bold mb-4 text-center">Welcome Back!</h2>

        <div className="bg-blue-900/50 rounded-lg p-4 mb-4">
          <p className="mb-3">While you were away, you have been mind-surfing and collecting Stoke 🤙.</p>

          <div className="flex flex-col gap-2 mb-4">
            <div className="flex justify-between items-center border-b border-blue-400/30 pb-2">
              <span>Time away:</span>
              <span className="font-semibold">{formatDuration(timeAwayInMinutes, false)}</span>
            </div>

            <div className="flex justify-between items-center border-b border-blue-400/30 pb-2">
              <span>Stoke multiplier:</span>
              <span className="font-semibold">{gameData.currencies.stoke.totalMultiplier}x</span>
            </div>

            <div className="flex justify-between items-center text-yellow-300 text-lg">
              <span className="font-bold">🤙 Stoke earned:</span>
              <span className="font-bold">{formatCurrency(offlineStoke || 0)}</span>
            </div>
          </div>

          <div className="text-sm opacity-75 italic mb-2">
            Your current Stoke: {formatCurrency(gameData.currencies.stoke.amount || 0)}/
            {formatCurrency(gameData.currencies.stoke.max || 1)}
          </div>
        </div>

        <div className="flex justify-center">
          <Button
            onClick={handleCollectStoke}
            className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-2 px-6 rounded-full"
          >
            Collect {formatCurrency(offlineStoke || 0)} Stoke
          </Button>
        </div>
      </div>
    </div>
  )
}
